/*
  SwingCoach — record or pick a swing video → send to the AI PGA coach
  →  render a beautifully-formatted analysis card (3 strengths, 3 fixes,
  a drill, and a tempo score).

  Two ways to enter:
    - From SwingLab (renders the coach card below the pose overlay).
    - From the in-round page (a compact "Analyse swing" chip that opens
      this component in a full-screen sheet).

  Video is uploaded to POST /api/swings/analyze which streams the file
  into Gemini Vision via emergentintegrations. The endpoint is Pro-only
  → free users see the 402 message with an upsell.
*/
import { useEffect, useRef, useState } from "react";
import { Camera, VideoCamera, Sparkle, X, ArrowClockwise, Trophy, Warning, Play } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import SwingStudio from "./SwingStudio";

const MAX_BYTES = 25 * 1024 * 1024;

export default function SwingCoach({ compact = false, onClose }) {
  // Kept for potential future programmatic re-triggers, but the picker
  // is currently driven by a native <label htmlFor> — see the input
  // below for why (iOS Safari won't open the chooser on JS-triggered
  // clicks against a display:none input).
  const inputRef = useRef(null);
  const [analysing, setAnalysing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState("");
  const [upsell, setUpsell] = useState(false);
  // Local blob URL for the just-uploaded swing so we can play it back
  // instantly without a round-trip. Revoked on unmount / re-record.
  const [localVideoUrl, setLocalVideoUrl] = useState("");
  // When true, mount the full-screen SwingStudio overlay for interactive
  // playback (pose overlay + phase icons + timeline callouts).
  const [studioOpen, setStudioOpen] = useState(false);

  useEffect(() => () => {
    if (localVideoUrl) {
      try { URL.revokeObjectURL(localVideoUrl); } catch { /* noop */ }
    }
  }, [localVideoUrl]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError("Video too large (max 25 MB). Try a shorter clip or lower quality.");
      return;
    }
    setError("");
    setUpsell(false);
    setAnalysis(null);
    setAnalysing(true);
    // Show the golfer their own clip immediately while Gemini is thinking.
    if (localVideoUrl) {
      try { URL.revokeObjectURL(localVideoUrl); } catch { /* noop */ }
    }
    setLocalVideoUrl(URL.createObjectURL(file));
    try {
      const form = new FormData();
      form.append("video", file);
      const { data } = await api.post("/swings/analyze", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000, // Gemini can take 30-60s on longer clips
      });
      setAnalysis(data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402) {
        setUpsell(true);
      } else if (detail) {
        setError(detail);
      } else {
        setError("Analysis failed — check your connection and try again.");
      }
    } finally {
      setAnalysing(false);
    }
  };

  return (
    <div className={compact ? "p-3" : "p-5"} data-testid="swing-coach">
      {!compact && (
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--accent-terracotta)" }}>
              <VideoCamera size={18} weight="fill" style={{ color: "#fff" }} />
            </div>
            <div>
              <h3 className="font-heading font-black text-lg text-[var(--primary)] leading-tight">Swing Coach</h3>
              <p className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">AI PGA analysis</p>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5" data-testid="swing-coach-close" aria-label="Close">
              <X size={18} weight="bold" />
            </button>
          )}
        </div>
      )}

      {/* Analysis result — highest-priority state, replaces the picker */}
      {analysis ? (
        <div className="space-y-3" data-testid="swing-coach-result">
          {localVideoUrl && (
            <button
              type="button"
              onClick={() => setStudioOpen(true)}
              className="relative w-full rounded-xl overflow-hidden bg-black aspect-video group"
              data-testid="swing-coach-open-studio"
            >
              <video
                src={localVideoUrl}
                muted
                playsInline
                className="w-full h-full object-contain"
                data-testid="swing-coach-video-player"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                <div className="flex items-center gap-2 bg-white text-black rounded-full px-4 py-2 font-heading font-bold text-sm shadow-lg">
                  <Play size={16} weight="fill" />
                  Open Swing Studio
                </div>
              </div>
              {(analysis.faults?.length || 0) > 0 && (
                <div className="absolute top-2 right-2 bg-black/70 text-white text-[10px] font-bold rounded-full px-2 py-0.5 uppercase tracking-wider" data-testid="swing-coach-fault-count">
                  {analysis.faults.length} callout{analysis.faults.length === 1 ? "" : "s"}
                </div>
              )}
            </button>
          )}
          <Section title="Doing well" icon={<Trophy size={14} weight="fill" />} tone="ok" items={analysis.strengths} testid="strengths" />
          <Section title="Work on" icon={<Warning size={14} weight="fill" />} tone="warn" items={analysis.improvements} testid="improvements" />
          {analysis.drill && (
            <div className="rounded-xl p-3" style={{ background: "var(--primary)", color: "#fff" }} data-testid="swing-coach-drill">
              <p className="text-[10px] uppercase tracking-widest opacity-70 font-bold">Try this drill</p>
              <p className="font-heading font-bold text-sm mt-1 leading-snug">{analysis.drill}</p>
              {analysis.drill_search_query && (
                <>
                  {/* In-app YouTube embed via nocookie iframe. Uses YouTube's
                      built-in search embed so we don't need a Data API key
                      — the iframe just plays the first relevant result. */}
                  <div className="mt-3 rounded-xl overflow-hidden aspect-video bg-black" data-testid="swing-coach-drill-embed">
                    <iframe
                      title="Drill demo"
                      src={`https://www.youtube-nocookie.com/embed?listType=search&list=${encodeURIComponent(analysis.drill_search_query)}&modestbranding=1&rel=0`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <a
                    href={`https://www.youtube.com/results?search_query=${encodeURIComponent(analysis.drill_search_query)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-[11px] font-bold underline opacity-80 hover:opacity-100"
                    style={{ color: "#fff" }}
                    data-testid="swing-coach-drill-more"
                  >
                    More drill videos on YouTube →
                  </a>
                </>
              )}
            </div>
          )}
          <div className="flex items-center justify-between rounded-xl p-3 bg-[var(--secondary)]">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] font-bold">Tempo score</p>
              <p className="font-mono-num font-black text-2xl text-[var(--primary)] leading-none mt-0.5">{analysis.tempo_score}/10</p>
            </div>
            <p className="text-[11px] text-[var(--text-secondary)] max-w-[55%] text-right leading-snug">{analysis.key_frame_notes}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setAnalysis(null);
              if (localVideoUrl) {
                try { URL.revokeObjectURL(localVideoUrl); } catch { /* noop */ }
                setLocalVideoUrl("");
              }
            }}
            className="w-full btn-secondary flex items-center justify-center gap-2"
            data-testid="swing-coach-again"
          >
            <ArrowClockwise size={14} weight="bold" /> Analyse another swing
          </button>
          <Link
            to="/app/swings"
            className="block text-center text-[11px] font-bold underline text-[var(--primary)] opacity-70 hover:opacity-100"
            data-testid="swing-coach-history-link"
          >
            View saved swings →
          </Link>
        </div>
      ) : upsell ? (
        <div className="rounded-xl p-4 text-center" style={{ background: "var(--accent-sand)" }} data-testid="swing-coach-upsell">
          <Sparkle size={22} weight="fill" className="mx-auto mb-1" style={{ color: "var(--accent-terracotta)" }} />
          <p className="font-heading font-bold text-[var(--primary)]">Swing Coach is a Pro feature</p>
          <p className="text-xs text-[var(--text-secondary)] mt-1 mb-3">Unlimited AI analyses, personalised drills, unlimited rounds.</p>
          <Link to="/app/pro" className="btn-primary inline-block" data-testid="swing-coach-upgrade">
            Upgrade to Pro
          </Link>
        </div>
      ) : (
        <div className="space-y-2" data-testid="swing-coach-picker">
          <p className="text-xs text-[var(--text-secondary)] leading-snug">
            Point the phone at your mate&apos;s swing (landscape, ~6-8 feet away, face-on or down-the-line). Tap below to record or pick a video — you&apos;ll get 3 strengths, 3 fixes, and a drill in ~15 seconds.
          </p>
          {/*
            IMPORTANT — the file input MUST stay in the flow (not `display:
            none`) or iOS Safari + some Android Chromes refuse to open the
            picker on a programmatic click. We use a <label htmlFor> so the
            OS opens the native chooser via the label click, which works
            reliably across browsers without any JS.

            `capture="environment"` was intentionally REMOVED — with it,
            some Chromes skipped the "Files" option and forced the camera
            open even when the user just wanted to pick an existing clip.
            Without it the OS shows both "Camera" and "Files" on mobile.
          */}
          <input
            ref={inputRef}
            id="swing-coach-video-input"
            type="file"
            accept="video/*"
            onChange={handleFile}
            style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            data-testid="swing-coach-file-input"
          />
          <label
            htmlFor="swing-coach-video-input"
            className={`w-full btn-primary flex items-center justify-center gap-2 cursor-pointer ${analysing ? "opacity-60 pointer-events-none" : ""}`}
            data-testid="swing-coach-pick"
          >
            <Camera size={16} weight="fill" />
            {analysing ? "Coach is watching…" : "Record or pick swing"}
          </label>
          {analysing && (
            <p className="text-center text-[11px] text-[var(--text-secondary)]" data-testid="swing-coach-progress">
              Sending to the AI coach — this takes 15-60 seconds.
            </p>
          )}
          {error && (
            <p className="text-center text-xs text-[var(--status-bad)] mt-2" data-testid="swing-coach-error">
              {error}
            </p>
          )}
        </div>
      )}
      {studioOpen && localVideoUrl && analysis && (
        <SwingStudio
          videoSrc={localVideoUrl}
          analysis={analysis}
          onClose={() => setStudioOpen(false)}
        />
      )}
    </div>
  );
}

function Section({ title, icon, tone, items, testid }) {
  const bg = tone === "ok" ? "rgba(16,185,129,.12)" : "rgba(196,84,60,.12)";
  const fg = tone === "ok" ? "#0F9C6C" : "var(--accent-terracotta)";
  return (
    <div className="rounded-xl p-3" style={{ background: bg }} data-testid={`swing-coach-${testid}`}>
      <div className="flex items-center gap-1.5 mb-1.5" style={{ color: fg }}>
        {icon}
        <p className="text-[10px] uppercase tracking-widest font-bold">{title}</p>
      </div>
      <ul className="space-y-1">
        {(items || []).slice(0, 3).map((s, i) => (
          <li key={i} className="text-xs leading-snug text-[var(--primary)] flex gap-1.5">
            <span className="opacity-40 mt-0.5">▸</span>
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
